import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DatabaseRecord, HasActivityLogs } from '../mixins';
import { AgencyBlocAgent } from './agency-bloc-agent';
import { AgencyBlocIndividual } from './agency-bloc-individual';
import { FormSubmissionType } from './form-submission-types';
import { HasEmailAttribute, HasFullNameAttribute } from './search-results-record';

export interface MeetingAgent {
  id: number;
  meeting_id: number;
  agency_bloc_agent_id: number | null;
  agency_bloc_individual_id: number | null;
  CustomerLastName: string;
  CustomerEmail: string;
  CustomerMobilePhone: string | null;
  CustomerName: string;
  CustomerCompany: string | null;
  CustomerPhone: string | null;
  CustomerNote: string | null;
  StartingTimeInCustomerTimeZone: string;
  CustomerTimeZone: string;
  CustomerCancelRescheduleLink: string;
  MeetingTimeInCustomerTimeZone: string;
  'ACE Advisor Meeting Location': string | null;
  'Advisor Email': string | null;
  'Advisor Phone Type': string | null;
  'Advisor Splits': string;
  created_at: string;
  updated_at: string;
  record?: AgencyBlocAgent;
}

export interface MeetingIndividual {
  id: number;
  meeting_id: number;
  agency_bloc_individual_id: number | null;
  'Birthdate Client 1': string | null;
  'Client Name': string;
  'Client Email': string | null;
  'Client Phone': string | null;
  'Client Phone Type': string | null;
  'Gender Client 1': string | null;
  'Birthdate Client 2': string | null;
  'Client Name Partner 2': string | null;
  'Client Email 2': string | null;
  'Client 2 Phone': string | null;
  'Client 2 Phone Type': string | null;
  'Gender Client 2': string | null;
  'Partner Status': string | null;
  created_at: string;
  updated_at: string;
  record?: AgencyBlocIndividual;
}

export interface MeetingLocation {
  id: number;
  meeting_id: number;
  'Meeting Location - Application': string | null;
  'Meeting Location - Phone Only': string | null;
  'Meeting Location - Webinar': string | null;
  'Meeting Location - Zoom Mtgs': string | null;
  'Meeting Location - Zoom Mtgs ADVISOR': string | null;
  'Meeting Location - Zoom Phone': string | null;
  'Meeting Location - Zoom Phone ADVISOR': string | null;
  'ACE Advisor Meeting Location': string | null;
  'ACE Client Meeting Location': string | null;
  created_at: string;
  updated_at: string;
}

export class Meeting extends DatabaseRecord(HasActivityLogs(HasEmailAttribute(HasFullNameAttribute()))) {
  public user_id = 0;
  public StartingTimeInOwnerTimeZone = '';
  public BookingPageName = '';
  public MeetingSubject = '';
  public MeetingDuration = '';
  public MeetingTimeInOwnerTimeZone = '';
  public MeetingCreationDate = '';
  public CustomerNote = '';
  public CustomerName = '';
  public ServiceName = '';
  public BookingPageOwner = '';
  public Description = '';
  public State?: string;
  public MeetingStatus = '';
  public BookingID = '';
  public BookingPageTimeZone = '';
  public Country?: string;
  public MeetingTime = '';
  public GoToMeetingorWebExlink?: string;
  public BookingPageLink = '';
  public MeetingLastUpdated = '';
  public ServiceDescription?: string;
  public ShortDescription = '';
  public MasterBookingPageLabel?: string;
  public Attachment?: string;
  public BookingInvoice = '';
  public MeetingLocation?: string;
  public ServiceCategory = '';
  public Location?: string;
  public ServicePrice = '';
  public VirtualLocation?: string;
  public NumberOfSessions = '';
  public 'Care Recipient Name'?: string;
  public BookingMode = '';
  public Label = '';
  public MeetingDurationSeconds = '';
  public MasterBookingPageName?: string;
  public 'Agreement Mtg Contact'?: string;
  public 'Agreement Mtg Contact Email'?: string;
  public BookingPageCategory = '';
  public 'Care Recipient Birthday'?: string;
  public 'Colleague Phone Type'?: string;
  public Conversation?: string;
  public Currency?: string;
  public 'Group Education Mtg Contact'?: string;
  public 'Group Education Mtg Contact Email'?: string;
  public 'LTCI Carrier'?: string;
  public 'LTCI Policy Number'?: string;
  public MasterBookingPageLink?: string;
  public 'Onboarding Mtg Contact'?: string;
  public 'Onboarding Mtg Contact Email'?: string;
  public 'Partner Status'?: string;
  public 'Policy Delivery Notes'?: string;
  public 'Policy Mail-to'?: string;
  public SessionPackageID?: string;
  public 'Thrivent Member'?: string;
  public 'Webinar Planning Mtg Contact'?: string;
  public 'Meeting Location - Zoom Mtgs'?: string;
  public 'Meeting Location - Zoom Phone ADVISOR'?: string;
  public 'Meeting Location - Zoom Mtgs ADVISOR'?: string;
  public 'Meeting Location - Zoom Phone'?: string;
  public 'Meeting Location - Phone Only'?: string;
  public 'Meeting Location - Application'?: string;
  public 'CancelRescheduleReason'?: string;
  public 'Advisor Splits'?: string;
  public 'Zoom Info'?: string;
  public utm_campaign?: string;
  public utm_content?: string;
  public utm_medium?: string;
  public utm_source?: string;
  public utm_term?: string;
  public wolf_archived = false;
  public agent_archived = false;
  public wolf_urgent = false;
  public agent_urgent = false;
  public 'meeting-slug'?: string;
  public MeetingTimeInCustomerTimeZone?: string;
  public CustomerTimeZone?: string;
  public location?: MeetingLocation;
  public agent?: MeetingAgent;
  public individual?: MeetingIndividual;
  public client?: MeetingIndividual;
  public Attendees?: string[];

  /**
   * The user's Meetings payload
   */
  public data?: MeetingIndividual;
  /**
   * Is this record checked in the UI?
   */
  public checked = false;

  /**
   * The form name for this meeting. This is the name of the Booking Page on OnceHub.
   */
  public form = '';

  /**
   * The user's FormSubmission payload
   */
  public partner?: FormSubmissionType;

  constructor(data?: Meeting) {
    super(data);
    Object.assign(this, data);
  }

  public createFormGroup(entityType?: string): FormGroup {
    if (entityType === 'Group') {
      return new FormGroup({
        groupName: new FormControl('', [Validators.required]),
        status: new FormControl(''),
        type: new FormControl(''),
        projectCode: new FormControl(''),
      });
    }
    const name = this.full_name.split(' ');
    const first_name = name[0];
    const last_name = name[name.length - 1];
    const middle_initial = name.length > 2 ? name[1] : '';
    return new FormGroup({
      firstName: new FormControl(first_name, [Validators.required]),
      middleInitial: new FormControl(middle_initial, [Validators.maxLength(1)]),
      lastName: new FormControl(last_name, [Validators.required]),
      email: new FormControl(this.email, [Validators.required, Validators.email]),
      status: new FormControl(''),
      type: new FormControl(''),
      projectCode: new FormControl(''),
    });
  }
}
